import { HoverCard } from "@/components/overview/hover-card";
import type { Domain, PhaseRecord } from "@/lib/api";
import {
  type DragKind,
  type Range,
  barBox,
  dragRange,
  keyDelta,
  mismatchText,
  pxToDays,
  rangeLabel,
  sameRange,
} from "@/lib/gantt";
import { css, cx } from "@styled-system/css";
import { useEffect, useRef } from "react";
import { MilestoneDiamond } from "./milestone-diamond";

export const DOMAIN_FILL: Record<Domain, string> = {
  cetec: css({ bg: "domain.cetec", borderColor: "domain.cetec" }),
  mkbd: css({ bg: "domain.mkbd", borderColor: "domain.mkbd" }),
  personal: css({ bg: "domain.personal", borderColor: "domain.personal" }),
};

export const DOMAIN_INK: Record<Domain, string> = {
  cetec: css({ color: "domain.cetec" }),
  mkbd: css({ color: "domain.mkbd" }),
  personal: css({ color: "domain.personal" }),
};

const HANDLE = 8;

const bar = css({
  appearance: "none",
  p: "0",
  m: "0",
  position: "absolute",
  top: "[6px]",
  bottom: "[6px]",
  zIndex: "[2]",
  borderRadius: "2",
  border: "[1px solid]",
  outline: "none",
  touchAction: "none",
  userSelect: "none",
  cursor: "grab",
  _focusVisible: { boxShadow: "[0 0 0 2px var(--oks-colors-border-focused)]" },
  _active: { cursor: "grabbing" },
});
const barSelected = css({ boxShadow: "[0 0 0 2px var(--oks-colors-border-selected)]" });
const barDone = css({ opacity: "0.35", cursor: "not-allowed" });
const barBlocked = css({ cursor: "not-allowed" });
const barEmpty = css({ bg: "[transparent]", borderStyle: "dashed" });
const barStaged = css({
  backgroundImage:
    "[repeating-linear-gradient(135deg, transparent 0 4px, rgba(255, 255, 255, 0.28) 4px 6px)]",
});

const handle = css({
  position: "absolute",
  top: "0",
  bottom: "0",
  w: "[8px]",
  cursor: "ew-resize",
  zIndex: "[1]",
});

const ghost = css({
  position: "absolute",
  top: "[4px]",
  bottom: "[4px]",
  zIndex: "[1]",
  borderRadius: "2",
  border: "[1px dashed]",
  pointerEvents: "none",
  opacity: "0.8",
});
const ghostWord = css({
  position: "absolute",
  top: "[50%]",
  left: "[100%]",
  transform: "translateY(-50%)",
  ml: "4",
  fontFamily: "mono",
  fontSize: "[10px]",
  lineHeight: "[12px]",
  color: "text.subtle",
  whiteSpace: "nowrap",
});

const outline = css({
  position: "absolute",
  top: "[6px]",
  bottom: "[6px]",
  zIndex: "[1]",
  borderRadius: "2",
  border: "[1px dotted]",
  borderColor: "border.bold",
  pointerEvents: "none",
});

const check = css({
  position: "absolute",
  top: "[50%]",
  transform: "translateY(-50%)",
  ml: "4",
  fontSize: "12",
  lineHeight: "[12px]",
  color: "text.success",
  zIndex: "[2]",
});

const flag = css({
  position: "absolute",
  top: "[50%]",
  zIndex: "[3]",
  transform: "translateY(-50%)",
});
const flagMark = css({
  display: "block",
  fontFamily: "mono",
  fontSize: "[11px]",
  lineHeight: "[14px]",
  fontWeight: "bold",
  color: "text.inverse",
  bg: "bg.warning.bold",
  borderRadius: "2",
  px: "3",
  outline: "none",
  cursor: "default",
  _focusVisible: { boxShadow: "[0 0 0 2px var(--oks-colors-border-focused)]" },
});

const problemText = css({
  position: "absolute",
  top: "[50%]",
  transform: "translateY(-50%)",
  left: "8",
  fontFamily: "mono",
  fontSize: "[11px]",
  color: "text.warning",
  whiteSpace: "nowrap",
});

export interface PhaseBarProps {
  phase: PhaseRecord;
  domain: Domain;
  /** The chart's day range and scale. */
  chart: Range;
  dayW: number;
  /** The phase's plan from the vault. Null when unreadable. */
  plan: Range | null;
  /** The staged range, if this phase has a staged change. */
  staged: Range | null;
  /** The live draft (drag, keys or the date panel), if any. */
  draft: Range | null;
  /** The range in an unapplied batch, if any. */
  pending: Range | null;
  /** Why it cannot be dragged, or null. */
  block: string | null;
  selected: boolean;
  onDraft: (phaseId: string, r: Range | null) => void;
  onDrop: (phaseId: string, r: Range) => void;
  onSelect: (phaseId: string) => void;
}

/**
 * One phase row's track content: the bar (drag body to move, edges to resize), its milestones,
 * the mismatch flag, a pending ghost, and the done check. Pointer, keyboard and click paths.
 */
export function PhaseBar(props: PhaseBarProps) {
  const { phase, domain, chart, dayW, plan, staged, draft, pending, block, selected } = props;
  const { onDraft, onDrop, onSelect } = props;
  const drag = useRef<{
    x: number;
    kind: DragKind;
    base: Range;
    moved: boolean;
    last: Range;
  } | null>(null);
  const shown = draft ?? staged ?? plan;

  // Esc during a pointer drag cancels it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && drag.current) {
        drag.current = null;
        onDraft(phase.id, null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDraft, phase.id]);

  if (!plan || !shown) {
    return (
      <span className={problemText}>
        {phase.problem ?? "no plan"}: {phase.planRaw} (see Problems)
      </span>
    );
  }

  const box = barBox(shown, chart, dayW);
  const label = `${phase.name}, ${rangeLabel(shown)}${staged && !draft ? ", staged" : ""}${
    phase.state === "done" ? ", done" : ""
  }${block ? `. ${block}` : ". Arrow keys move it, Shift for a week, Alt for the end. Enter stages."}`;

  const start = (e: React.PointerEvent, kind: DragKind) => {
    if (block || e.button !== 0) return;
    e.stopPropagation();
    try {
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    } catch {
      // A synthetic or already-released pointer cannot be captured. The drag still works.
    }
    drag.current = { x: e.clientX, kind, base: shown, moved: false, last: shown };
  };
  const move = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x;
    if (Math.abs(dx) > 3) d.moved = true;
    if (!d.moved) return;
    const next = dragRange(d.base, d.kind, pxToDays(dx, dayW));
    if (!sameRange(next, d.last)) {
      d.last = next;
      onDraft(phase.id, next);
    }
  };
  const end = () => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    if (!d.moved) {
      onDraft(phase.id, null);
      onSelect(phase.id);
      return;
    }
    onDraft(phase.id, null);
    onDrop(phase.id, d.last);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (draft) onDrop(phase.id, draft);
      else onSelect(phase.id);
      return;
    }
    if (e.key === "Escape") {
      onDraft(phase.id, null);
      return;
    }
    const k = keyDelta(e.key, { shift: e.shiftKey, alt: e.altKey });
    if (!k || block) return;
    e.preventDefault();
    onDraft(phase.id, dragRange(draft ?? shown, k.kind, k.days));
  };

  const mismatch = mismatchText(phase);
  const done = phase.state === "done";

  return (
    <>
      {!sameRange(shown, plan) && (
        <span
          className={outline}
          title={`Plan in the vault: ${rangeLabel(plan)}`}
          style={{ left: barBox(plan, chart, dayW).x, width: barBox(plan, chart, dayW).w }}
        />
      )}
      {pending && (
        <span
          className={cx(ghost, DOMAIN_INK[domain])}
          style={{ left: barBox(pending, chart, dayW).x, width: barBox(pending, chart, dayW).w }}
        >
          <span className={ghostWord}>pending</span>
        </span>
      )}
      <button
        type="button"
        aria-label={label}
        aria-pressed={selected}
        aria-disabled={block ? true : undefined}
        title={
          block ?? `${rangeLabel(shown)}. Drag to move, drag an edge to resize, click for dates.`
        }
        data-phase={phase.id}
        className={cx(
          bar,
          DOMAIN_FILL[domain],
          phase.state === "empty" && barEmpty,
          staged && !draft && barStaged,
          done && barDone,
          block && !done && barBlocked,
          selected && barSelected
        )}
        style={{ left: box.x, width: box.w }}
        onPointerDown={(e) => start(e, "move")}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={() => {
          drag.current = null;
          onDraft(phase.id, null);
        }}
        onClick={(e) => {
          // A blocked bar still opens its panel, which says why it cannot move.
          if (block) onSelect(phase.id);
          e.stopPropagation();
        }}
        onKeyDown={onKeyDown}
      >
        {!block && (
          <>
            <span
              className={handle}
              style={{ left: -1 }}
              data-handle="start"
              onPointerDown={(e) => start(e, "start")}
            />
            <span
              className={handle}
              style={{ right: -1, width: Math.min(HANDLE, Math.max(4, box.w / 3)) }}
              data-handle="end"
              onPointerDown={(e) => start(e, "end")}
            />
          </>
        )}
      </button>
      {done && (
        <span className={check} style={{ left: box.x + box.w }} aria-hidden="true">
          ✓
        </span>
      )}
      {phase.milestones.map((m) => (
        <MilestoneDiamond
          key={m.taskId}
          text={m.text}
          due={m.due}
          open={m.open}
          x={barBox({ start: m.due, end: m.due }, chart, dayW).x + dayW / 2}
          outside={m.due < shown.start || m.due > shown.end}
          ink={DOMAIN_INK[domain]}
        />
      ))}
      {mismatch && (
        // Inside the bar's end, so it never covers a diamond just past the end.
        <div className={flag} style={{ left: box.x + box.w - 16 }}>
          <HoverCard
            title="Plan and milestone disagree"
            lines={[mismatch, "Shown only. Edit the note to fix it."]}
          >
            <span
              // biome-ignore lint/a11y/noNoninteractiveTabindex: focus shows the hover card for keyboard users
              tabIndex={0}
              role="img"
              aria-label={`Mismatch: ${mismatch}`}
              className={flagMark}
            >
              ≠
            </span>
          </HoverCard>
        </div>
      )}
    </>
  );
}
